import { Action, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Params } from "react-router";
import { getMeRequest } from "Api/me/getMe.request";

import { getAuthUserRequest } from "Api/authUser/getAuthUser.request";
import config from "console_config";
import logger from "Libs/logger";
import { routerLocationChange } from "Reducers/app/routerLocationChange";
import { getFeatureFlag } from "Reducers/featureFlags";
import { loadUserProfile } from "Reducers/profile";
import { loadAllProjects } from "Reducers/project/project";
import { getRegions } from "Reducers/project/region";
import { AsyncThunkOptionType } from "Reducers/types";
import { identifyPendo } from "Store/analytics/pendoTracker";

import { loadSubscriptions } from "../subscription";

import { selectCurrentUserUsername } from "./selectors";
import { isInternalUser } from "./utils";

import type { OrganizationProfile } from "@packages/client";
import type { AuthUser } from "Api/authUser/types";
import { loadOrganizations } from "Reducers/organization/organization/thunks/loadOrganizations.thunk";
import type { CurrentUser } from "Api/me/types";

export interface LocationAction extends Action {
  payload: {
    location: {
      pathname: string;
    };
  };
}

declare global {
  interface Window {
    pendo: any;
    drift: any;
    zE: any;
  }
}

//where should it be called? trial info is in organizationprofile but we still need me
export const setUserAttributesDrift = ({
  me,
  organizationProfile,
  user
}: {
  me?: CurrentUser;
  organizationProfile?: OrganizationProfile;
  user?: AuthUser;
}) => {
  if (!config.ID_DRIFT_CHATBOT || !window.drift) return;
  window.drift.on("ready", api => {
    api.setUserAttributes({
      me_id: me?.id,
      trial_start_date:
        organizationProfile?.current_trial?.created &&
        new Date(organizationProfile?.current_trial?.created).toISOString(),
      trial_end_date:
        organizationProfile?.current_trial?.expiration &&
        new Date(organizationProfile?.current_trial?.expiration).toISOString(),
      created_at: user?.created_at && new Date(user?.created_at).toISOString()
    });
    window.drift.page();
  });
};
export const identifyDrift = (me: CurrentUser) => {
  if (!config.ID_DRIFT_CHATBOT || !window.drift || !me.id) return;

  window.drift.identify(me.id, {
    is_internal: isInternalUser(me.email) ? "platformer" : "normal account"
  });
  window.drift.load(config.ID_DRIFT_CHATBOT);

  setUserAttributesDrift({ me });
};

export const getAccountInfo = createAsyncThunk<
  CurrentUser,
  undefined,
  AsyncThunkOptionType
>("app/get_account_info", async (_, { rejectWithValue }) => {
  const organizationEnabled = getFeatureFlag("ENABLE_ORGANIZATION");

  try {
    // getUser require a uuid, but to get current user we can call users/me
    const userMe = await getAuthUserRequest("me");
    if (organizationEnabled) {
      return userMe;
    }
    // Legacy vendors (ENABLE_ORGANIZATION off) still need the accounts /me
    // payload for `projects` and `uuid`; see CON-1610.
    const me = await getMeRequest();
    return {
      ...userMe,
      ...me,
      created_at: userMe.created_at,
      username: userMe.username
    };
  } catch (error) {
    logger(error, {
      action: "GET_ACCOUNT_INFO"
    });
    return rejectWithValue(error);
  }
});

export const init = createAsyncThunk<
  undefined,
  boolean | undefined,
  AsyncThunkOptionType
>("app/get_me", async (_, { dispatch, getState, rejectWithValue }) => {
  const organizationEnabled = getFeatureFlag("ENABLE_ORGANIZATION");

  try {
    // Load the current user
    const { payload: me } = await dispatch(getAccountInfo());

    if (me) {
      identifyPendo({
        id: me.id,
        createdAt: me.created_at,
        email: me.email
      });
      identifyDrift(me);
    }

    if (organizationEnabled) {
      dispatch(loadOrganizations({ userId: me.id }));
    } else {
      // Load all the users subscriptions at first load to avoid race condition with loadSubscription
      const username = selectCurrentUserUsername(getState());
      dispatch(loadSubscriptions({ organizationId: username }));
    }
  } catch (error) {
    logger(error, {
      action: "GET_ME_START"
    });
    return rejectWithValue(error);
  } finally {
    setTimeout(() => {
      dispatch(loadUserProfile());
      if (!organizationEnabled) {
        dispatch(loadAllProjects());
      }
      dispatch(getRegions());
    }, 0);
  }
});

interface AppSliceState {
  loading: boolean;
  error: unknown;
  me?: CurrentUser;
  routeHistory: {
    params: Readonly<Params<string>>;
    currentRoute: string;
    previousRoutes: string[];
  };
}

const initialState: AppSliceState = {
  loading: false,
  error: null,
  routeHistory: {
    params: {},
    currentRoute: "",
    previousRoutes: []
  }
};

const app = createSlice({
  name: "app",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // INIT
      .addCase(init.pending, state => {
        state.loading = true;
      })
      .addCase(init.fulfilled, state => {
        state.routeHistory.currentRoute = window.location.pathname;
      })
      .addCase(init.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // GET ACCOUNT INFO
      .addCase(getAccountInfo.pending, state => {
        state.loading = true;
      })
      .addCase(getAccountInfo.fulfilled, (state, action) => {
        state.me = action.payload;
        state.loading = false;
      })
      .addCase(getAccountInfo.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
    builder.addCase(routerLocationChange, (state, action) => {
      // This action doesn't contain the previous route so we have to log each
      // route in the store, then move it to a list that contains the history.
      // Then ensure no duplicates are added to the history.
      state.routeHistory.params = action.payload.params;
      state.routeHistory.currentRoute = action.payload.location.pathname;
      state.routeHistory.previousRoutes = Array.from(
        new Set([
          state.routeHistory.currentRoute,
          ...state.routeHistory.previousRoutes
        ])
      );
    });
  }
});

export default app.reducer;
